import { useState, useEffect, useCallback, useRef } from "react";

const noop = () => {};
const noopAsync = async () => null;

// Electron wraps a rejected IPC handler as
// "Error invoking remote method 'serial:connect': Error: <real message>".
// Strip that wrapper so the UI shows the actionable text we threw in main.
const cleanIpcError = (message = "") =>
  String(message)
    .replace(/^Error invoking remote method '[^']*':\s*/i, "")
    .replace(/^Error:\s*/i, "");

export const useSerialPort = () => {
  const api = window.electronAPI?.serial;
  const isElectron = !!window.electronAPI?.isElectron;

  const [ports, setPorts] = useState([]);
  const [connected, setConnected] = useState(false);
  const [currentPort, setCurrentPort] = useState(null);
  const [receivedData, setReceivedData] = useState([]);
  const [error, setError] = useState(null);

  const cleanupRef = useRef([]);
  // Side channel for the calibration `live` gauge (~100 Hz). Only the latest
  // line is kept; it is NOT appended to receivedData, so it can't flood the
  // console view or trigger 100 Hz whole-tree re-renders. Consumers read the
  // latest via requestAnimationFrame.
  const liveLineRef = useRef(null);

  useEffect(() => {
    if (!api) return;

    const removeDataListener = api.onData((data) => {
      if (typeof data === "string" && data.includes('"t":"live"')) {
        liveLineRef.current = data;
        return;
      }
      setReceivedData((prev) => [...prev, { timestamp: Date.now(), data }]);
    });

    const removeDisconnectListener = api.onDisconnect(() => {
      setConnected(false);
      setCurrentPort(null);
    });

    cleanupRef.current = [removeDataListener, removeDisconnectListener];

    return () => {
      cleanupRef.current.forEach((fn) => fn());
      cleanupRef.current = [];
    };
  }, [api]);

  const listPorts = useCallback(async () => {
    if (!api) return [];
    try {
      setError(null);
      const result = await api.listPorts();
      setPorts(result);
      return result;
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [api]);

  const connect = useCallback(
    // 115200 per the wearable console contract. Baud is nominally irrelevant on
    // native USB CDC, but we match the device's stated rate rather than rely on
    // a legacy default.
    async (path, baudRate = 115200) => {
      if (!api) return null;
      try {
        setError(null);
        const result = await api.connect(path, baudRate);
        setConnected(true);
        setCurrentPort(path);
        setReceivedData([]);
        return result;
      } catch (err) {
        setError(cleanIpcError(err.message));
        return null;
      }
    },
    [api]
  );

  const disconnect = useCallback(async () => {
    if (!api) return null;
    try {
      setError(null);
      const result = await api.disconnect();
      setConnected(false);
      setCurrentPort(null);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [api]);

  const send = useCallback(
    async (command) => {
      if (!api) return null;
      try {
        setError(null);
        return await api.send(command);
      } catch (err) {
        setError(err.message);
        return null;
      }
    },
    [api]
  );

  // Write an exact string verbatim (no terminator added). For the diagnostic
  // raw console — lets us try different line endings.
  const sendRaw = useCallback(
    async (text) => {
      if (!api?.sendRaw) return null;
      try {
        setError(null);
        return await api.sendRaw(text);
      } catch (err) {
        setError(err.message);
        return null;
      }
    },
    [api]
  );

  // Pulse DTR low→high without closing the port. The v0.5.15 console only
  // drains its RX buffer at a session-start edge; this synthesizes one.
  const kick = useCallback(async () => {
    if (!api?.kick) return null;
    try {
      setError(null);
      return await api.kick();
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [api]);

  const clearData = useCallback(() => {
    setReceivedData([]);
  }, []);

  // Binary-safe export: the main process reads the raw stream (v3's inline
  // record bytes can contain 0x0A) and returns record blocks. Returns
  // { banner, records:[{index,id,sess,len,byteCount,crc,crcOk,bytesB64}],
  //   aborted, refused, done } or null when the API is unavailable.
  const exportRecords = useCallback(
    async (opts) => {
      if (!api?.exportRecords) return null;
      try {
        setError(null);
        return await api.exportRecords(opts);
      } catch (err) {
        setError(cleanIpcError(err.message));
        throw err;
      }
    },
    [api]
  );

  // Subscribe to export progress (records received so far). Returns unsubscribe.
  const onExportProgress = useCallback(
    (cb) => api?.onExportProgress?.(cb) || noop,
    [api]
  );

  // Subscribe to a wearable being plugged in. Returns an unsubscribe fn.
  const onWearableAttached = useCallback(
    (cb) => api?.onWearableAttached?.(cb) || noop,
    [api]
  );

  // Subscribe to the port set changing (device plugged/unplugged). The callback
  // receives the fresh port list; we also fold it into `ports` so the scan list
  // live-refreshes without a manual rescan. Returns an unsubscribe fn.
  const onPortsChanged = useCallback(
    (cb) =>
      api?.onPortsChanged?.((list) => {
        setPorts(list);
        cb?.(list);
      }) || noop,
    [api]
  );

  if (!isElectron) {
    return {
      isAvailable: false,
      ports: [],
      connected: false,
      currentPort: null,
      receivedData: [],
      error: null,
      liveLineRef,
      listPorts: noopAsync,
      connect: noopAsync,
      disconnect: noopAsync,
      send: noopAsync,
      sendRaw: noopAsync,
      kick: noopAsync,
      clearData: noop,
      exportRecords: noopAsync,
      onExportProgress: () => noop,
      onWearableAttached: () => noop,
      onPortsChanged: () => noop,
    };
  }

  return {
    isAvailable: true,
    ports,
    connected,
    currentPort,
    receivedData,
    error,
    liveLineRef,
    listPorts,
    connect,
    disconnect,
    send,
    sendRaw,
    kick,
    clearData,
    exportRecords,
    onExportProgress,
    onWearableAttached,
    onPortsChanged,
  };
};
